/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { customAlphabet } from "nanoid";
import React, { useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import { useRecoilState, useSetRecoilState } from "recoil";
import { updateSavedSearch } from "../../../../api/SavedSearches";
import { updateUser } from "../../../../api/User";
import { CountedSavedSearchesAtom } from "../../../../atoms/SavedSearches";
import { UserAtom } from "../../../../atoms/UserAtom";
import { IUserIRoute } from "../../../../generated/Api";
import { Flex } from "../../../../styles/general";
import { SavedSearch } from "../../../../types/SavedSearchTypes";
import { FormatEmail } from "../../../../utils/FormatEmail";
import { LINE_SEPARATOR_OPTIONS } from "../../../Modal/container/EmailSettings/container/FooterSettings";
import { useModal } from "../../../Modal/Modal";
import StyledAutocompleteSelect from "../../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../../StyledButton";
import StyledInput from "../../../StyledInput";
import StyledRichInput from "../../../StyledRichInput";
import {
  Bottom,
  Container,
  Header,
  HeaderTitle,
  SavedSearchModal,
  Wrapper,
} from "../SavedSearches";
export const nanoidForFooterAndAlias = customAlphabet(
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz-",
  16
);

const FormGroup = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  margin-bottom: 20px;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #2f2f2f;
  letter-spacing: 0.18px;
  margin-bottom: 10px;
`;

const TitleDescription = styled.div`
  color: #bebebe;
  font-size: 13px;
  margin-left: 5px;
`;

type Props =
  | {
      search: SavedSearch;
      isEdit: true;
      searchIsEdit?: boolean;
      handlePage: (page: SavedSearchModal) => void;
      handleSavedSearch: (search: SavedSearch) => void;
    }
  | {
      search?: SavedSearch;
      isEdit?: boolean;
      searchIsEdit?: boolean;
      handlePage: (page: SavedSearchModal) => void;
      handleSavedSearch: (search: SavedSearch) => void;
    };

export default function FooterSearchModal(props: Props): JSX.Element {
  const { search, isEdit, searchIsEdit, handlePage, handleSavedSearch } = props;
  const setCountedSavedSearches = useSetRecoilState(CountedSavedSearchesAtom);
  const [user, setUser] = useRecoilState(UserAtom);
  const modal = useModal();
  const [isLoading, setIsLoading] = useState(false);

  const [footerOptions, setFooterOptions] = useState(
    isEdit
      ? user!.emailOptions!.footers!.find((f) => f.id === search.footerId)!
      : {
          id: undefined,
          signature: "",
          name: "",
          additionalFooterText: "",
          textBeforeUnsubscribe: "",
          textAfterUnsubscribe: "",
          hasSeparatorLine: null,
        }
  );

  const {
    signature,
    name,
    additionalFooterText,
    textBeforeUnsubscribe,
    textAfterUnsubscribe,
    hasSeparatorLine,
  } = footerOptions;

  const { footer } = FormatEmail.format("", user as IUserIRoute, {
    footer: footerOptions || user?.emailOptions.footers[0],
  });

  function updateUserFooter(footerId: string) {
    setCountedSavedSearches((s) =>
      s.map((searchFromMap) => {
        if (searchFromMap.id === search.id)
          return { ...searchFromMap, footerId };
        return searchFromMap;
      })
    );

    handleSavedSearch({ ...search, footerId });
  }

  async function createOrSave() {
    if (!user || isLoading) return;

    const alreadyExist = user.emailOptions.footers.find(
      (f) =>
        f.name.toLowerCase().trim() ===
          footerOptions.name.toLowerCase().trim() && f.id !== footerOptions.id
    );

    if (alreadyExist) {
      toast("Footer with this name already exists", { type: "error" });
      setIsLoading(false);
      return;
    }

    if (!isEdit) {
      const id = nanoidForFooterAndAlias();
      const isSuccessUpdateUser = await updateUser({
        emailOptions: {
          ...user?.emailOptions,
          footers: [...user?.emailOptions?.footers!, { ...footerOptions, id }],
        },
      });

      if (isSuccessUpdateUser) {
        setUser(
          (u) =>
            ({
              ...u,
              emailOptions: {
                ...u?.emailOptions,
                footers: [
                  ...u?.emailOptions?.footers!,
                  { ...footerOptions, id },
                ],
              },
            } as any)
        );

        if (searchIsEdit) {
          const isSuccessUpdateSearch = await updateSavedSearch(search.id, {
            name: search.name,
            publicArchive: search.publicArchive,
            footerId: id,
          });

          if (isSuccessUpdateSearch) {
            updateUserFooter(id);
            toast("Footer created", { type: "success" });
            handlePage(SavedSearchModal.NEW_EDIT);
          }
        } else {
          updateUserFooter(id);
          toast("Footer created", { type: "success" });
          handlePage(SavedSearchModal.NEW_EDIT);
        }
      }
    } else {
      const newFooterArray = user.emailOptions.footers.map((f) => {
        if (f.id !== footerOptions.id) return f;
        return footerOptions;
      });

      const isSuccessUpdateUser = await updateUser({
        emailOptions: {
          ...user.emailOptions,
          footers: newFooterArray,
        },
      });

      if (isSuccessUpdateUser) {
        setUser((u) => ({
          ...u,
          emailOptions: { ...user.emailOptions, footers: newFooterArray },
        }));

        if (searchIsEdit) {
          const isSuccessUpdateSearch = await updateSavedSearch(search.id, {
            name: search.name,
            publicArchive: search.publicArchive,
            footerId: footerOptions.id,
          });

          if (isSuccessUpdateSearch) {
            updateUserFooter(footerOptions.id);
            toast("Footer updated", { type: "success" });
            handlePage(SavedSearchModal.NEW_EDIT);
          }
        } else {
          updateUserFooter(footerOptions.id);
          toast("Footer updated", { type: "success" });
          handlePage(SavedSearchModal.NEW_EDIT);
        }
      }
    }

    setIsLoading(false);
  }

  return (
    <Container>
      <Header>
        <Flex>
          {
            <div
              css={css`
                cursor: pointer;
                height: 15px;
              `}
              onClick={() => handlePage(SavedSearchModal.NEW_EDIT)}
            >
              <FaChevronLeft color="#BBBBBB" size={15} />
            </div>
          }

          <HeaderTitle
            css={css`
              margin-left: 5px;
            `}
          >
            {isEdit ? "Edit Footer" : "New Footer"}
          </HeaderTitle>
        </Flex>
        <div
          css={css`
            cursor: pointer;
          `}
        >
          <IoMdClose
            color="#868482"
            size={25}
            onClick={() => {
              modal.popModal();
            }}
          />
        </div>
      </Header>
      <Wrapper>
        <FormGroup
          css={css`
            margin-top: 20px;
          `}
        >
          <Flex
            css={css`
              margin-bottom: 8px;
            `}
          >
            <Title
              css={css`
                margin-bottom: 0px;
              `}
            >
              Footer Name
            </Title>
          </Flex>
          <StyledInput
            value={name}
            disabled={user.emailOptions.footers[0].id === footerOptions.id}
            onChange={(evt) =>
              setFooterOptions((f) => ({ ...f, name: evt.target.value }))
            }
            placeholder="Enter footer name"
          />
        </FormGroup>

        <FormGroup>
          <Flex
            css={css`
              margin-bottom: 8px;
            `}
          >
            <Title
              css={css`
                margin-bottom: 0px;
              `}
            >
              Email Signature
            </Title>
            <TitleDescription>(Optional)</TitleDescription>
          </Flex>
          <StyledRichInput
            placeholder="Enter email signature"
            value={signature}
            onChange={(value) =>
              setFooterOptions((f) => ({
                ...f,
                signature: value.replace("<p>", "").replace("</p>", ""),
              }))
            }
          />
        </FormGroup>

        <FormGroup>
          <Flex
            css={css`
              margin-bottom: 8px;
            `}
          >
            <Title
              css={css`
                margin-bottom: 0px;
              `}
            >
              Additional footer text
            </Title>
            <TitleDescription>(Optional)</TitleDescription>
          </Flex>
          <StyledRichInput
            placeholder="Enter additional footer text"
            value={additionalFooterText}
            onChange={(value) =>
              setFooterOptions((f) => ({
                ...f,
                additionalFooterText: value
                  .replace("<p>", "")
                  .replace("</p>", ""),
              }))
            }
          />
        </FormGroup>

        <FormGroup>
          <Flex
            css={css`
              margin-bottom: 8px;
            `}
          >
            <Title
              css={css`
                margin-bottom: 0px;
              `}
            >
              Text before unsubscribe link
            </Title>
            <TitleDescription>(Optional)</TitleDescription>
          </Flex>
          <StyledRichInput
            placeholder="Enter text before Unsubscribe link"
            value={textBeforeUnsubscribe}
            onChange={(value) =>
              setFooterOptions((f) => ({
                ...f,
                textBeforeUnsubscribe: value
                  .replace("<p>", "")
                  .replace("</p>", ""),
              }))
            }
          />
        </FormGroup>

        <FormGroup>
          <Flex
            css={css`
              margin-bottom: 8px;
            `}
          >
            <Title
              css={css`
                margin-bottom: 0px;
              `}
            >
              Text after unsubscribe link
            </Title>
            <TitleDescription>(Optional)</TitleDescription>
          </Flex>
          <StyledRichInput
            placeholder="Enter text after Unsubscribe link"
            value={textAfterUnsubscribe}
            onChange={(value) =>
              setFooterOptions((f) => ({
                ...f,
                textAfterUnsubscribe: value
                  .replace("<p>", "")
                  .replace("</p>", ""),
              }))
            }
          />
        </FormGroup>

        <FormGroup>
          <Title>Line separator</Title>
          <StyledAutocompleteSelect
            options={LINE_SEPARATOR_OPTIONS}
            selectedOption={LINE_SEPARATOR_OPTIONS.find(
              (l) => l.value === hasSeparatorLine
            )}
            onOptionSelect={(vle) => {
              if (vle.value === "") return;

              setFooterOptions((f) => ({
                ...f,
                hasSeparatorLine: vle.value as any,
              }));
            }}
            placeholder="Type or select an option"
          />
        </FormGroup>
        <FormGroup>
          <Title>Preview</Title>

          <div
            css={css`
              white-space: pre-wrap;
              background-color: #f4f4f4;
              padding: 10px;
              border-radius: 4px;
              width: 100%;
            `}
            dangerouslySetInnerHTML={{ __html: footer }}
          />
        </FormGroup>
      </Wrapper>

      <Bottom>
        <StyledButton
          inverted
          onClick={() => modal.popModal()}
          style={{ marginRight: "10px", width: "92px" }}
        >
          Cancel
        </StyledButton>
        <StyledButton
          style={{ width: "92px" }}
          onClick={() => {
            if (isLoading) return;
            setIsLoading(true);
            createOrSave();
          }}
          isLoading={isLoading}
        >
          {isEdit ? "Save" : "Create"}
        </StyledButton>
      </Bottom>
    </Container>
  );
}
